import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import './EnhancedDataTable.css';

/**
 * @summary Enhanced data table component with sorting, filtering, and pagination
 * @param {array} data - Array of data objects to display
 * @param {array} columns - Array of column configuration objects
 * @param {object} filters - Filter criteria
 * @param {number} itemsPerPage - Number of items to display per page
 * @returns {React.ReactElement} Returns the EnhancedDataTable component
 */
const EnhancedDataTable = ({ data, columns, filters, itemsPerPage: defaultItemsPerPage = 15 }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
  const prevDataRef = useRef([]);
  const prevFiltersRef = useRef({});

  // Reset to first page when data or filters change
  useEffect(() => {
    const dataChanged = data !== prevDataRef.current;
    const filtersChanged = JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current);
    
    if (dataChanged || filtersChanged) {
      // console.log('Data or filters changed, resetting to first page');
      setCurrentPage(1);
      prevDataRef.current = data;
      prevFiltersRef.current = filters;
    }
  }, [data, filters]);

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(Number(newSize));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Helper function to parse date strings in MM/DD/YYYY format
  const parseDate = useCallback((dateString) => {
    if (!dateString || typeof dateString !== 'string') return null;
    const parts = dateString.trim().split('/');
    if (parts.length !== 3) return null;
    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    if (isNaN(month) || isNaN(day) || isNaN(year)) return null;
    return new Date(year, month - 1, day);
  }, []);

  // Helper function to check if a column is a date column
  const isDateColumn = useCallback((key) => {
    const column = columns.find(col => col.key === key);
    return column && column.type === 'date';
  }, [columns]);

  // Filter and sort data
  const filteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) {
      // console.warn('Invalid data provided to EnhancedDataTable:', data);
      return [];
    }

    // console.log(`Filtering ${data.length} records with filters:`, filters);

    // Apply filters
    let result = [...data];

    // Filter out expired programs
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to beginning of day

    result = result.filter(item => {
      // Check all date columns for expiration
      for (const column of columns) {
        if (column.type === 'date') {
          const dateValue = item[column.key];
          if (dateValue) {
            const parsedDate = parseDate(dateValue);
            if (parsedDate && parsedDate < today) {
              return false; // Exclude expired programs
            }
          }
        }
      }
      return true;
    });

    if (filters && Object.keys(filters).length > 0) {
      result = result.filter(item => {
        return Object.entries(filters).every(([key, value]) => {
          // Skip empty filter values
          if (!value || (Array.isArray(value) && value.length === 0)) {
            return true;
          }

          const itemValue = item[key];

          // Handle different filter types
          if (Array.isArray(value)) {
            // Multi-select filter
            return value.includes(itemValue);
          } else if (typeof value === 'string') {
            // Text filter - case insensitive search
            return itemValue &&
              itemValue.toString().toLowerCase().includes(value.toLowerCase());
          }

          return true;
        });
      });
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';

        // Handle date comparison
        if (isDateColumn(sortConfig.key)) {
          const aDate = parseDate(aValue);
          const bDate = parseDate(bValue);

          // Handle null dates (put them at the end)
          if (!aDate && !bDate) return 0;
          if (!aDate) return 1;
          if (!bDate) return -1;

          if (sortConfig.direction === 'asc') {
            return aDate - bDate;
          } else {
            return bDate - aDate;
          }
        }

        // Handle string comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          if (sortConfig.direction === 'asc') {
            return aValue.localeCompare(bValue);
          } else {
            return bValue.localeCompare(aValue);
          }
        }

        // Handle numeric comparison
        if (sortConfig.direction === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }

    // console.log(`After filtering: ${result.length} records`);
    return result;
  }, [data, filters, sortConfig, columns, isDateColumn, parseDate]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Generate pagination controls
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    const pages = [];
    const maxVisiblePages = 5;
    
    // Calculate range of visible pages
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    // Add first page
    if (startPage > 1) {
      pages.push(
        <button 
          key="first" 
          onClick={() => handlePageChange(1)}
          aria-label="First page"
        >
          &laquo;
        </button>
      );
    }
    
    // Add previous page
    if (currentPage > 1) {
      pages.push(
        <button 
          key="prev" 
          onClick={() => handlePageChange(currentPage - 1)}
          aria-label="Previous page"
        >
          &lsaquo;
        </button>
      );
    }
    
    // Add page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button 
          key={i} 
          onClick={() => handlePageChange(i)}
          className={currentPage === i ? 'active' : ''}
          aria-current={currentPage === i ? 'page' : null}
        >
          {i}
        </button>
      );
    }
    
    // Add next page
    if (currentPage < totalPages) {
      pages.push(
        <button 
          key="next" 
          onClick={() => handlePageChange(currentPage + 1)}
          aria-label="Next page"
        >
          &rsaquo;
        </button>
      );
    }
    
    // Add last page
    if (endPage < totalPages) {
      pages.push(
        <button 
          key="last" 
          onClick={() => handlePageChange(totalPages)}
          aria-label="Last page"
        >
          &raquo;
        </button>
      );
    }
    
    return (
      <div className="pagination">
        <div className="pagination-info">
          Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredData.length)} of {filteredData.length} records
        </div>
        <div className="page-size-selector">
          <label htmlFor="page-size">Show:</label>
          <select 
            id="page-size"
            value={itemsPerPage} 
            onChange={(e) => handlePageSizeChange(e.target.value)}
            className="page-size-dropdown"
          >
            <option value="15">15</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
          <span>per page</span>
        </div>
        <div className="pagination-controls">
          {pages}
        </div>
      </div>
    );
  };

  return (
    <div className="enhanced-data-table">
      {filteredData.length > 0 ? (
        <>
          <div className="table-container">
            <table>
              <caption className="sr-only">
                {`Training programs table with ${filteredData.length} ${filteredData.length === 1 ? 'result' : 'results'}. ${
                  sortConfig.key
                    ? `Sorted by ${sortConfig.key} in ${sortConfig.direction === 'asc' ? 'ascending' : 'descending'} order.`
                    : 'Not sorted.'
                }`}
              </caption>
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      scope="col"
                      role="columnheader"
                      className={`${column.sortable ? 'sortable' : ''} ${column.align ? `align-${column.align}` : 'align-center'}`}
                      onClick={column.sortable ? () => handleSort(column.key) : undefined}
                      onKeyDown={column.sortable ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSort(column.key);
                        }
                      } : undefined}
                      tabIndex={column.sortable ? 0 : -1}
                      aria-sort={
                        column.sortable && sortConfig.key === column.key
                          ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending')
                          : column.sortable ? 'none' : undefined
                      }
                    >
                      {column.header}
                      {column.sortable && sortConfig.key === column.key && (
                        <span className={`sort-indicator ${sortConfig.direction}`} aria-hidden="true">
                          {sortConfig.direction === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item, index) => (
                  <tr key={index}>
                    {columns.map((column) => (
                      <td key={`${index}-${column.key}`} className={column.align ? `align-${column.align}` : ''}>
                        {column.render ? column.render(item[column.key], item) : item[column.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </>
      ) : (
        <div className="no-data">
          <p>No results found. Please try different filter criteria.</p>
        </div>
      )}
    </div>
  );
};

EnhancedDataTable.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      render: PropTypes.func,
      type: PropTypes.string,
      align: PropTypes.string
    })
  ).isRequired,
  filters: PropTypes.object,
  itemsPerPage: PropTypes.number
};

export default EnhancedDataTable;
